import { EmailVerification } from '../components/EmailVerification';
import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Clock, Pencil, Heart, UserRound, ChevronRight, Mail, CalendarDays } from 'lucide-react';
import { api } from '../api';
import { usePlatform } from '../store';
import { UserAvatar } from '../components/UserAvatar';
import { GameArt } from '../components/GameCard';
import { gameTitle } from '../game-presentation';
import { Achievements } from './Achievements';
import './profile.css';
import './avatar-settings.css';

interface PlayStats {
  todaySeconds: number;
  totalSeconds: number;
  games: { gameId: string; seconds: number }[];
}
function playTime(seconds: number) {
  if (seconds > 0 && seconds < 60) return '不足 1 分钟';
  const minutes = Math.floor(seconds / 60);
  return minutes >= 60
    ? `${Math.floor(minutes / 60)} 小时 ${minutes % 60} 分钟`
    : `${minutes} 分钟`;
}
function StatTime({ seconds }: { seconds: number }) {
  const minutes = Math.floor(seconds / 60);
  if (seconds > 0 && seconds < 60) return <em>不足 1 分钟</em>;
  return (
    <>
      {minutes >= 60 && (
        <span className="resident-time-unit">
          <b>{Math.floor(minutes / 60)}</b>
          <em>小时</em>
        </span>
      )}
      <span className="resident-time-unit">
        <b>{minutes % 60}</b>
        <em>分钟</em>
      </span>
    </>
  );
}
function IslandStatIcon() {
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <path d="M4 54Q15 45 26 49Q44 44 60 54Q47 61 32 57Q17 62 4 54" fill="#ffdd89" />
      <path d="M6 49Q12 40 20 43Q20 33 29 36Q38 37 40 49Q28 57 6 51" fill="#199bea" />
      <path
        d="M15 48q4-5 9 0m5-2q3-4 6 0"
        fill="none"
        stroke="#066dcc"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M36 49Q42 32 35 15"
        fill="none"
        stroke="#9f794d"
        strokeWidth="5"
        strokeLinecap="round"
      />
      <path
        d="M35 17Q21 0 13 14Q25 10 33 21Q13 10 12 29Q23 18 34 22Q28 24 27 38Q39 29 37 21Q47 23 52 34Q58 16 38 17Q48 7 57 15Q52 0 36 14Z"
        fill="#479564"
      />
      <path d="M36 17Q29 3 22 5Q31 8 34 20" fill="#7ab24e" />
    </svg>
  );
}
export function LegacyAchievements() {
  const [params] = useSearchParams();
  const target = new URLSearchParams(params);
  target.set('tab', 'achievements');
  return <Navigate to={'/account/profile?' + target.toString()} replace />;
}
export function Profile() {
  const [params] = useSearchParams();
  const achievements = params.get('tab') === 'achievements';
  const { data, toggleFavorite } = usePlatform();
  const [collection, setCollection] = useState<'recents' | 'favorites'>('recents');
  const [stats, setStats] = useState<PlayStats | null>(null);
  const [statsError, setStatsError] = useState(false);
  useEffect(() => {
    let alive = true;
    setStats(null);
    setStatsError(false);
    if (data?.user)
      api<PlayStats>('/me/play-stats')
        .then((value) => {
          if (alive) setStats(value);
        })
        .catch(() => {
          if (alive) setStatsError(true);
        });
    return () => {
      alive = false;
    };
  }, [data?.user?.id]);
  if (!data) return <div className="loading-panel">正在加载…</div>;
  if (!data.user)
    return (
      <Navigate
        to={
          '/login?next=' +
          encodeURIComponent('/account/profile' + (params.size ? '?' + params.toString() : ''))
        }
        replace
      />
    );
  const { user } = data;
  const games = data[collection].flatMap((id) => {
    const game = data.games.find((g) => g.id === id);
    return game ? [game] : [];
  });
  const joined =
    user.createdAt && !Number.isNaN(Date.parse(user.createdAt))
      ? new Date(user.createdAt).toLocaleDateString('zh-CN', {
          year: 'numeric',
          month: 'long',
        })
      : '尚无记录';
  return (
    <section className="resident-page">
      <div className="resident-cover" aria-hidden="true" />
      <div className="resident-identity">
        <UserAvatar src={user.avatarUrl} size={100} />
        <div className="resident-name">
          <h1>
            {user.nickname || user.username}
            <span className="resident-badge">岛民</span>
          </h1>
          <p>@{user.username}</p>
        </div>
        <Link className="button resident-edit" to="/account/profile/edit">
          <Pencil size={15} />
          编辑资料
        </Link>
      </div>
      <nav className="resident-sections" aria-label="个人主页">
        <Link to="/account/profile" aria-current={!achievements ? 'page' : undefined}>
          我的游戏
        </Link>
        <Link
          to="/account/profile?tab=achievements"
          aria-current={achievements ? 'page' : undefined}
        >
          我的成就
        </Link>
      </nav>
      {achievements ? (
        <Achievements inProfile />
      ) : (
        <>
          <section
            className="resident-stats"
            aria-label="我的摸鱼统计"
            title="按已记录会话统计；今日按北京时间的会话开始日期归属，可能包含暂停及多标签重叠时间。"
          >
            <div>
              <i>
                <IslandStatIcon />
              </i>
              <span>
                今日摸鱼
                <strong>
                  {stats ? (
                    <StatTime seconds={stats.todaySeconds} />
                  ) : (
                    <em>{statsError ? '暂不可用' : '加载中…'}</em>
                  )}
                </strong>
              </span>
            </div>
            <div>
              <i>
                <Clock />
              </i>
              <span>
                累计摸鱼<strong>{stats ? <StatTime seconds={stats.totalSeconds} /> : '—'}</strong>
              </span>
            </div>
            <div>
              <i className="stat-gamepad">
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  <path
                    d="M17 14h30q7 0 9 10l5 24q1 11-8 8L40 45H24L11 56q-9 3-8-8l5-24q2-10 9-10"
                    fill="#26988b"
                  />
                  <path
                    d="M19 25v14m-7-7h14"
                    stroke="#e3fff4"
                    strokeWidth="4"
                    strokeLinecap="round"
                  />
                  <circle cx="45" cy="26" r="3" fill="#e3fff4" />
                  <circle cx="51" cy="35" r="3" fill="#e3fff4" />
                </svg>
              </i>
              <span>
                玩过的游戏
                <strong>
                  {stats ? (
                    <>
                      <b>{stats.games.length}</b>
                      <em>款</em>
                    </>
                  ) : (
                    '—'
                  )}
                </strong>
              </span>
            </div>
            <div>
              <i className="stat-heart">
                <svg viewBox="0 0 64 64" aria-hidden="true">
                  <defs>
                    <linearGradient id="profile-heart" x2="0.8" y2="1">
                      <stop stopColor="#ff7795" />
                      <stop offset="1" stopColor="#ff3d6b" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M32 55 9 33C-5 18 15-1 32 15 49-1 69 18 55 33Z"
                    fill="url(#profile-heart)"
                  />
                  <ellipse
                    cx="18"
                    cy="19"
                    rx="3"
                    ry="5"
                    fill="#ffb1c2"
                    transform="rotate(28 18 19)"
                  />
                </svg>
              </i>
              <span>
                最爱玩的游戏
                <strong className="resident-top-game">
                  {stats?.games[0]?.seconds
                    ? (() => {
                        const game = data.games.find((g) => g.id === stats.games[0].gameId);
                        return game ? gameTitle(game) : '已下架游戏';
                      })()
                    : stats
                      ? '等待探索'
                      : '—'}
                </strong>
                <small>
                  {stats?.games[0]?.seconds
                    ? `累计 ${playTime(stats.games[0].seconds)}`
                    : '按累计游玩时长'}
                </small>
              </span>
            </div>
          </section>
          <div className="resident-columns">
            <aside className="resident-about">
              <section className="resident-panel">
                <h3>
                  <UserRound size={23} fill="currentColor" />
                  岛民资料
                </h3>
                <dl>
                  <div>
                    <dt>账号</dt>
                    <dd>{user.username}</dd>
                  </div>
                  <div>
                    <dt>
                      <Mail size={14} />
                      邮箱
                    </dt>
                    <dd>{user.email || '未填写'}</dd>
                  </div>
                  <div>
                    <dt>
                      <CalendarDays size={14} />
                      加入小岛
                    </dt>
                    <dd>{joined}</dd>
                  </div>
                </dl>
                <EmailVerification compact />
                <div className="resident-preferences">
                  <div className="resident-section-title">
                    <h3>
                      <Heart size={23} fill="currentColor" />
                      我的游戏偏好
                    </h3>
                  </div>
                  <div className="resident-tags">
                    {user.favoriteTags?.length ? (
                      user.favoriteTags.map((tag) => <span key={tag}>{tag}</span>)
                    ) : (
                      <p>还没选好？慢慢发现你的喜欢。</p>
                    )}
                  </div>
                </div>
              </section>
            </aside>
            <section className="resident-panel resident-collection">
              <div className="resident-collection-head">
                <div className="resident-tabs" role="group" aria-label="游戏记录">
                  <button
                    aria-pressed={collection === 'recents'}
                    onClick={() => setCollection('recents')}
                  >
                    最近游玩
                  </button>
                  <button
                    aria-pressed={collection === 'favorites'}
                    onClick={() => setCollection('favorites')}
                  >
                    我的收藏
                  </button>
                </div>
                <Link
                  className="resident-all"
                  to={collection === 'recents' ? '/recent' : '/favorites'}
                >
                  查看全部
                  <ChevronRight size={15} />
                </Link>
              </div>
              {games.length ? (
                <div className="resident-games">
                  {games.slice(0, 3).map((game) => (
                    <article key={game.id} className="resident-game">
                      <Link to={'/game/' + game.slug} aria-label={gameTitle(game)}>
                        <GameArt
                          game={game}
                          sizes="(max-width: 600px) 112px, (max-width: 1000px) 28vw, 290px"
                        />
                      </Link>
                      <div>
                        <strong>
                          <Link to={'/game/' + game.slug}>{gameTitle(game)}</Link>
                        </strong>
                        <small>{game.categories.join(' / ')}</small>
                        <small className="resident-playtime">
                          <Clock size={14} />
                          {stats
                            ? `已玩 ${playTime(stats.games.find((g) => g.gameId === game.id)?.seconds || 0)}`
                            : '时长暂不可用'}
                        </small>
                      </div>
                      <button
                        className="resident-favorite"
                        aria-label={`${data.favorites.includes(game.id) ? '取消收藏' : '收藏'}${gameTitle(game)}`}
                        aria-pressed={data.favorites.includes(game.id)}
                        onClick={() => void toggleFavorite(game)}
                      >
                        <Heart
                          size={20}
                          fill={data.favorites.includes(game.id) ? 'currentColor' : 'none'}
                        />
                      </button>
                    </article>
                  ))}
                </div>
              ) : (
                <div className="resident-empty">
                  <p>{collection === 'recents' ? '暂无游玩记录' : '暂无收藏'}</p>
                  <Link to="/library">
                    去游戏库
                    <ChevronRight size={14} />
                  </Link>
                </div>
              )}
            </section>
          </div>
        </>
      )}
    </section>
  );
}
